'use client';

import Image from 'next/image';
import { CurrencySymbol, useFormatPrice } from 'hooks/pricing/useFormattedPrice';
import type { Currency } from 'utils/Currency';
import { useBuyContext } from '../../state/BuyContext';
import { useBuySelection } from '../../state/useBuySelection';
import { resolve, type ImageCardConfig } from '../../state/cardSchema';
import { Section } from '../Section';
import styles from './ImageOptionCard.module.css';

export type ImageOptionCardProps = {
  card: ImageCardConfig;
};

export const ImageOptionCard = ({ card }: ImageOptionCardProps) => {
  const { resolveCtx, currency } = useBuyContext();
  const { selected, select } = useBuySelection(card.id);
  const formatPriceWithComma = useFormatPrice();
  const symbol = CurrencySymbol[currency.toLowerCase() as Currency];

  return (
    <Section id={card.id} title={card.title} subtitle={card.subtitle} info={card.info}>
      <div className={styles.grid} role="radiogroup" aria-label={typeof card.title === 'string' ? card.title : card.id}>
        {card.options.map((option) => {
          const isActive = selected === option.id;
          const disabled = !!resolve(option.disabled, resolveCtx);
          const price = resolve(option.price, resolveCtx);

          return (
            <button
              key={option.id}
              type="button"
              className={styles.card}
              data-active={isActive ? 'true' : 'false'}
              onClick={() => select(option.id)}
              disabled={disabled}
              role="radio"
              aria-checked={isActive}
            >
              {option.tag ? <span className={styles.tag}>{option.tag}</span> : null}
              <span className={styles['image-wrap']}>
                <Image
                  src={option.image}
                  alt={typeof option.title === 'string' ? option.title : ''}
                  width={120}
                  height={120}
                />
              </span>
              <span className={styles.title}>{option.title}</span>
              {option.subTitle ? <span className={styles.sub}>{option.subTitle}</span> : null}
              {typeof price === 'number' ? (
                <span className={styles.price}>
                  {price === 0 ? (
                    'FREE'
                  ) : (
                    <>
                      {symbol}
                      {formatPriceWithComma(price)}
                    </>
                  )}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>
    </Section>
  );
};
